// Tipos y utilidades que comparten las pantallas comerciales (Publicaciones, Leads, Pedidos y Mi sitio).
import { useSearchParams } from 'react-router';

export interface Publicacion {
  id: string;
  tipo: 'auto' | 'pieza';
  vehiculoId: string;
  piezaId: string | null;
  titulo: string;
  precioMxn: string;
  vin: string | null;
  estado: 'borrador' | 'publicada' | 'retirada' | 'vendida';
  motivoRetiro: string | null;
  /** Rutas `/medios/…` de las fotos subidas o direcciones https de antes (2.19). */
  fotos: string[];
  creadaEn: string;
  publicadaEn: string | null;
}
export interface Lead {
  id: string;
  publicacionTitulo: string | null;
  origen: 'tienda' | 'marketplace' | 'canal';
  canal: string | null;
  tipo: 'pregunta' | 'cita' | 'apartado';
  nombre: string;
  telefono: string | null;
  correo: string | null;
  mensaje: string;
  fechaCita: string | null;
  estado: 'nuevo' | 'contactado' | 'cerrado' | 'descartado';
  asignadoA: string | null;
  nota: string | null;
  creadoEn: string;
}
export interface Pedido {
  id: string;
  folio: string;
  origen: string;
  estado: string;
  siguientes: string[];
  comprador: { nombre: string; telefono: string | null; correo: string | null };
  entrega: 'recoleccion' | 'envio';
  direccion: { calle: string; ciudad: string; estado: string; codigoPostal: string } | null;
  totalMxn: string;
  nota: string | null;
  motivoCancelacion: string | null;
  venceEn: string;
  vencido: boolean;
  creadoEn: string;
  partidas: { publicacionId: string; tipo: string; titulo: string; precioMxn: string }[];
}
/** Identidad de la tienda (arquitectura 2.19.2), como la guarda commerce-svc. */
export interface IdentidadTienda {
  quienesSomos?: string;
  historia?: string;
  firma?: string;
  fundadoEn?: number;
  servicios?: { titulo: string; descripcion?: string; precioDesde?: string }[];
  redes?: Partial<Record<'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'mercadoLibre', string>>;
  cifras?: { valor: string; etiqueta: string }[];
  equipo?: {
    nombre: string;
    puesto?: string;
    nota?: string;
    foto?: string;
    fotoUrl?: string | null;
  }[];
  garantia?: string;
  senales?: ('pedimento' | 'historial' | 'garantia' | 'envios' | 'facturacion')[];
}

export interface Sitio {
  slug: string;
  nombreComercial: string;
  lema: string | null;
  descripcion: string | null;
  ciudad: string;
  estadoRegion: string | null;
  telefono: string | null;
  pais?: string;
  telefonoWhatsapp?: boolean;
  telefonoPrefijo?: string;
  whatsappPrefijo?: string;
  whatsapp: string | null;
  correo: string | null;
  colores: { primario: string; secundario: string } | null;
  logoUrl: string | null;
  portadaUrl: string | null;
  /** Imágenes subidas (2.19): identificadores de Media & Documents. */
  logoImagen?: string | null;
  portadaImagen?: string | null;
  galeriaIds?: string[];
  horario: string | null;
  direccion: string | null;
  enfoque: 'autos' | 'piezas' | 'mixto' | null;
  identidad?: IdentidadTienda;
  /** La elegida (nula: la de su tipo) y con la que de verdad abre según su plan. */
  plantilla?: string | null;
  plantillaEfectiva?: string;
  publicado: boolean;
  publicadoEn: string | null;
}

export const ESTADO_PUBLICACION: Record<Publicacion['estado'], [string, string]> = {
  borrador: ['Borrador', 'aviso'],
  publicada: ['Publicada', 'exito'],
  retirada: ['Retirada', ''],
  vendida: ['Vendida', ''],
};
export const ESTADO_PEDIDO: Record<string, string> = {
  apartado: 'Apartado',
  confirmado: 'Confirmado',
  listo_entrega: 'Listo para entregar',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};
export const ESTADO_LEAD: Record<Lead['estado'], string> = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  cerrado: 'Cerrado',
  descartado: 'Descartado',
};

export const fechaHora = (iso: string) =>
  new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City',
  }).format(new Date(iso));

/** Filtro por estado en la URL (se conserva al recargar). */
export function useFiltro() {
  const [params, setParams] = useSearchParams();
  const estado = params.get('estado') ?? '';
  const poner = (valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set('estado', valor);
    else p.delete('estado');
    setParams(p);
  };
  return [estado, poner] as const;
}
